// One detail route, four kinds — with a deliberate information hierarchy.
//
// The old version put a title, then a stat strip, then fields, then tags, then the
// timeline last, all in one narrow column: everything looked equally important, so
// nothing did. The structure now:
//
//   · a header BAND — who this is, its labels, the one action, on a ruled ground
//   · a MAIN column that opens with the timeline, because the notes ARE the
//     description of a record; then the sections specific to the kind
//   · a sticky ASIDE holding the facts: fields, rollups, tags, relations, backlinks
//
// Facts stop competing with prose for attention by being somewhere else, and the
// thing you actually came to read or write is at the top.

import { useMemo, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'

import { AddRecord } from '../components/AddRecord'
import { AsideField } from '../components/AsideField'
import { Backlinks } from '../components/Backlinks'
import { Commitments, DeliveryNote } from '../components/Commitments'
import {
  ActorStamp,
  ConfirmAction,
  EditableText,
  EmptyState,
  Pill,
  PillSelect,
  Section,
} from '../components/common'
import { EntityLink } from '../components/EntityLink'
import { EntryThread } from '../components/EntryThread'
import { Icon, iconName } from '../components/Icon'
import { LinksField } from '../components/Links'
import { Markdown } from '../components/Markdown'
import { MentionInput } from '../components/MentionInput'
import { NetworkCanvas } from '../components/NetworkCanvas'
import { AsideBlock, Page, useStickyHeight } from '../components/Page'
import { RefPicker } from '../components/RefPicker'
import { RelationField } from '../components/RelationField'
import { useCreateEntity } from '../components/useCreate'
import type { Draft } from '../lib/events'
import { formatShortDate, money, todayIso } from '../lib/format'
import { newId } from '../lib/ids'
import {
  accentVar,
  isCommitted,
  KINDS,
  labelOf,
  titleField,
  type EntityKind,
  type FieldDef,
  type StageKind,
} from '../lib/model'
import { buildNetwork, neighbourhood } from '../lib/network'
import {
  chainValue,
  commitmentsForDeal,
  commitmentsForWork,
  dealsForProject,
  descriptionOf,
  fieldStr,
  connectionsFor,
  linkedOfKind,
  orgsForDeal,
  projectsForDeal,
  renewalChain,
  reuseScore,
  stageOf,
  groupsForWork,
  isDelivered,
  orgsForProject,
  orgsForArtifact,
  deliveryOf,
  shownCount,
  titleOf,
  type EntityRecord,
  type State,
} from '../lib/reducer'
import { useStore } from '../lib/store'
import { Board } from './BoardPage'

export function EntityDetail() {
  const { id = '' } = useParams()
  // An org has one page, and it is the client page. This route stays so every
  // existing link, mention and bookmark still lands somewhere — it just lands on
  // the page that can actually do something.
  if (id.startsWith('org_')) return <Navigate to={`/c/${id}`} replace />
  // Key by id so switching records remounts and every uncontrolled editor resets.
  return <Detail key={id} id={id} />
}

function Detail({ id }: { id: string }) {
  const { state, version } = useStore()
  void version
  const rec = state.entities[id]

  if (!rec) {
    return (
      <Page>
        <EmptyState icon="Search" title="Not found" hint="It may have been archived." />
      </Page>
    )
  }

  return (
    <Page
      // Every record belongs to a client, and every page now says which. This used
      // to run for projects only, so a deal, an artifact or a person was a dead end
      // with nothing but the back button — and the back button is undo, not
      // navigation.
      aside={<DetailAside rec={rec} />}
      banner={<DetailHeader rec={rec} crumbs={crumbsFor(state, rec)} />}
    >
      {/* Each kind opens with what that kind is FOR. Before this, all five ran
          description → timeline → network in one fixed order and differed only by
          which optional block appeared halfway down, which is why they were hard to
          tell apart: they were the same page with different middles. */}
      {rec.kind === 'deal' && <DealHero deal={rec} />}
      {rec.kind === 'artifact' && <ArtifactHero artifact={rec} />}

      {/* Its own words first on a project: what this IS has to come before a grid
          of rows about it. The board was above because it is what you came for on a
          busy project — but on a new one it is an empty table sitting on top of the
          only sentence that says what the project is. Two lines of prose cost the
          board almost nothing; the reverse was not true. */}
      <Description rec={rec} />

      {/* A board reachable from exactly one screen is a board nobody finds, so a
          project carries its own here. */}
      {rec.kind === 'project' && <ProjectBoard project={rec} />}

      {rec.kind === 'deal' && <DealCommitments deal={rec} />}

      <Section title="Timeline" note="filed here, plus anything that mentions this">
        <EntryThread subjectId={id} />
      </Section>

      {rec.kind === 'project' && <ProjectSections project={rec} />}
      <LocalNetwork id={id} />
    </Page>
  )
}

/**
 * Where a record sits, as one chain back to its client.
 *
 * Every kind resolves to at most one client, which is what makes the client the
 * spine of the app rather than one tab among nine:
 *
 *   deal      → the org it is with
 *   project   → the org whose deals pay for it
 *   artifact  → the project it came out of, and that project's org
 *   person    → the org they are linked to
 *
 * Clients is always the first crumb even when nothing else resolves, so the trail
 * never renders as a lone orphan.
 */
function crumbsFor(state: State, rec: EntityRecord): Array<{ label: string; to: string }> {
  const trail: Array<{ label: string; to: string }> = [{ label: 'Clients', to: '/orgs' }]
  const org = (o: EntityRecord | undefined) => {
    if (o) trail.push({ label: fieldStr(o, 'name'), to: `/c/${o.id}` })
  }

  if (rec.kind === 'deal') org(orgsForDeal(state, rec.id)[0])
  else if (rec.kind === 'project') org(orgsForProject(state, rec.id)[0])
  else if (rec.kind === 'person') org(linkedOfKind(state, rec.id, 'org')[0])
  else if (rec.kind === 'artifact') {
    org(orgsForArtifact(state, rec.id)[0])
    const from = linkedOfKind(state, rec.id, 'project')[0]
    if (from) trail.push({ label: fieldStr(from, 'name'), to: `/e/${from.id}` })
  }
  return trail
}

/**
 * A deal, opened with the ladder and the money.
 *
 * Both used to be aside blocks a third of the way down the rail, at the same weight
 * as "Mentioned by". A deal page answers "where is this and what is owed", so the
 * two answers go across the top, full width, and the track stays clickable because
 * moving a deal along is the main thing you do on it.
 *
 * `Lost` is not drawn as a rung: it is an exit, not a step, and stretching the
 * track to hold it made the six real stages narrower for no gain.
 */
function DealHero({ deal }: { deal: EntityRecord }) {
  const { state, config, dispatch } = useStore()
  const current = stageOf(deal, config.stages)
  const ladder = config.stages.filter((s) => s.kind !== 'lost')
  const at = ladder.findIndex((s) => s.key === current)
  const lost = config.stages.find((s) => s.key === current)?.kind === 'lost'
  const delivery = deliveryOf(state, deal.id)
  const chain = renewalChain(state, deal.id)
  const value = Number(deal.fields.value) || 0

  const move = (to: string) => {
    if (to === current) return
    const drafts: Draft[] = [
      { type: 'deal.stage_change', subject: deal.id, data: { from: current, to } },
    ]
    // Committing is what turns a deal into a contract: stamp the date once, here.
    if (isCommitted(config.stages.find((s) => s.key === to)?.kind) && !fieldStr(deal, 'signedAt')) {
      drafts.push({ type: 'deal.update', subject: deal.id, data: { signedAt: todayIso() } })
    }
    void dispatch(drafts)
  }

  return (
    <section
      className="rounded-control mb-6 border px-4 py-3.5"
      style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
    >
      {lost && (
        <p className="eyebrow mb-2" style={{ color: 'var(--crit)' }}>
          Lost · kept, because knowing why is worth more than a tidy list
        </p>
      )}
      <div className="flex gap-[3px]" style={{ opacity: lost ? 0.4 : 1 }}>
        {ladder.map((s, i) => (
          <button
            key={s.key}
            type="button"
            onClick={() => move(s.key)}
            title={s.description}
            aria-label={s.label}
            className="h-1.5 flex-1 rounded-full"
            style={{
              background:
                i < at
                  ? 'color-mix(in srgb, var(--ember) 45%, transparent)'
                  : i === at
                    ? 'var(--ember)'
                    : 'var(--rule-soft)',
              boxShadow:
                i === at
                  ? '0 0 0 3px color-mix(in srgb, var(--ember) 20%, transparent)'
                  : undefined,
            }}
          />
        ))}
      </div>
      <div className="mt-1.5 flex gap-[3px]">
        {ladder.map((s, i) => (
          <span
            key={s.key}
            className="flex-1 truncate text-center"
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 'var(--text-micro)',
              color: i === at ? 'var(--ember)' : 'var(--faint)',
              fontWeight: i === at ? 500 : 400,
            }}
          >
            {s.label}
          </span>
        ))}
      </div>

      <div
        className="mt-3.5 flex flex-wrap gap-x-8 gap-y-3 border-t pt-3"
        style={{ borderColor: 'var(--rule-soft)' }}
      >
        <HeroNum label="Value" value={value ? money(value, '·') : '·'} tone="var(--ember)" />
        <HeroNum label="Signed" value={dateOrDash(fieldStr(deal, 'signedAt'))} />
        <HeroNum label="Ends" value={dateOrDash(fieldStr(deal, 'endsAt'))} />
        {delivery.items.length > 0 && (
          <HeroNum
            label="Owed"
            value={
              <>
                {delivery.owed}{' '}
                <span className="text-body" style={{ color: 'var(--muted)' }}>
                  of {delivery.items.length}
                </span>
              </>
            }
          />
        )}
        {chain.length > 1 && (
          <HeroNum
            label="Renewals"
            value={String(chain.length)}
            sub={money(chainValue(state, deal.id, config), '·') + ' lifetime'}
          />
        )}
      </div>
    </section>
  )
}

function HeroNum({
  label,
  value,
  tone,
  sub,
}: {
  label: string
  value: React.ReactNode
  tone?: string | undefined
  sub?: string | undefined
}) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div
        className="num text-title leading-tight tracking-tight"
        style={tone ? { color: tone } : undefined}
      >
        {value}
      </div>
      {sub && <div className="meta">{sub}</div>}
    </div>
  )
}

function dateOrDash(iso: string): string {
  return iso ? formatShortDate(iso) : '·'
}

/**
 * An artifact's one section: who has seen it.
 *
 * This opened with a strip of tiles — Delivered / Kind / Came out of / Shown to —
 * and every one of them was already in the aside, where it is editable. A read-only
 * copy of an editable field, drawn twice as large, is worse than not drawing it: it
 * is the first thing you try to click and the one thing that does nothing.
 *
 * So the facts stay in the aside and the page opens with the thing that is NOT a
 * field: the `showing` edges, which exist in the model and had no surface at all.
 * On an artifact they are the most interesting fact on the page and the seed of the
 * lineage story in positioning.md §5.
 */
function ArtifactHero({ artifact }: { artifact: EntityRecord }) {
  const { state } = useStore()
  const orgs = orgsForArtifact(state, artifact.id)

  return (
    <Section title="Shown to" note="who has seen this">
      {orgs.length === 0 ? (
        <p className="note py-2">
          Not shown to anyone yet. Name a client in the rail, or attach the project it came out of.
        </p>
      ) : (
        <div className="rows">
          {orgs.map((o) => (
            <Link key={o.id} to={`/c/${o.id}`} className="flex items-baseline gap-3 py-2">
              <span className="text-body min-w-0 flex-1 font-medium">{fieldStr(o, 'name')}</span>
              <Icon name="ArrowRight" size={14} style={{ color: 'var(--faint)' }} />
            </Link>
          ))}
        </div>
      )}
    </Section>
  )
}

/**
 * What a deal promised. Shown once the deal is committed — a running contract's most
 * active question is what's owed and when — and kept visible on a finished one. On an
 * open deal it's one quiet line, because you sometimes want to draft the deliverables
 * into a proposal before anyone signs.
 */
function DealCommitments({ deal }: { deal: EntityRecord }) {
  const { state, config } = useStore()
  const [drafting, setDrafting] = useState(false)
  const kind = config.stages.find((s) => s.key === stageOf(deal, config.stages))?.kind
  const started = isCommitted(kind) || kind === 'closed'
  const items = commitmentsForDeal(state, deal.id)

  if (!started && items.length === 0 && !drafting) {
    return (
      <Section title="Deliverables">
        <p className="text-body py-1" style={{ color: 'var(--faint)' }}>
          Nothing promised yet.{' '}
          <button
            type="button"
            onClick={() => setDrafting(true)}
            className="hover:underline"
            style={{ color: 'var(--accent)' }}
          >
            Add one
          </button>
          .
        </p>
      </Section>
    )
  }

  return (
    <Section
      title="Deliverables"
      // "Commitment" is the record and stays the record — it is one type where
      // atlas had Deliverable and Milestone, and the order-of-existence argument
      // (promise a thing before it exists, attach work later or never) is why. But
      // the word never said CONTRACTUAL, which is the only thing that separates one
      // of these from a board row, so the UI says the word people actually use.
      note="what this deal owes, with a tick when delivered"
      right={<DeliveryNote dealId={deal.id} />}
    >
      <Commitments dealId={deal.id} />
    </Section>
  )
}

const PROSE_PLACEHOLDER: Record<EntityKind, string> = {
  project: 'What this is. @mention the work it builds on.',
  artifact: 'What it is, and what it was for. @mention the work it came out of.',
  deal: 'What is being sold, and on what terms.',
  org: 'Who they are.',
  person: 'Who they are.',
}

/**
 * The prose field, first in the main column. There used to be one of these per kind
 * under a different name each time — `summary` and `context` on work, `about` on orgs
 * and people, `description` on deals — which meant four names for one idea and, on
 * work, two boxes competing to be the real one. Writes go to `description`; the old
 * keys are still read (descriptionOf) so nothing is lost.
 */
function Description({ rec }: { rec: EntityRecord }) {
  const { state, dispatch } = useStore()
  // Read back out of the prose above, both directions. A mention is an upstream
  // claim, so what this builds on is what it says, and what it informs is every
  // other record that said it.
  const web = connectionsFor(state, rec.id)
  const up = web.filter((c) => c.to === rec.id).map((c) => c.from)
  const down = web.filter((c) => c.from === rec.id).map((c) => c.to)

  return (
    <Section title="Description">
      <RichField
        value={descriptionOf(rec)}
        placeholder={PROSE_PLACEHOLDER[rec.kind] ?? ''}
        onSave={(v) =>
          void dispatch({ type: `${rec.kind}.update`, subject: rec.id, data: { description: v } })
        }
      />
      {(up.length > 0 || down.length > 0) && (
        <p className="text-body mt-2.5" style={{ color: 'var(--muted)' }}>
          {up.length > 0 && (
            <>
              Builds on{' '}
              {up.map((pid, i) => (
                <span key={pid}>
                  {i > 0 && ', '}
                  <EntityLink id={pid} />
                </span>
              ))}
              .{' '}
            </>
          )}
          {down.length > 0 && (
            <>
              Informs{' '}
              {down.map((pid, i) => (
                <span key={pid}>
                  {i > 0 && ', '}
                  <EntityLink id={pid} />
                </span>
              ))}
              .
            </>
          )}
        </p>
      )}
    </Section>
  )
}

/**
 * The record header: full width above both columns, dense, and unchanging. Nothing
 * about it reflows on scroll — a header that changes size at a scroll threshold reads
 * as a click into place however carefully the threshold is damped, so this one is
 * simply small enough to keep.
 */
function DetailHeader({
  rec,
  crumbs,
}: {
  rec: EntityRecord
  crumbs: Array<{ label: string; to: string }>
}) {
  // Publishes its own height, so the aside beside it sticks below rather than under.
  const box = useStickyHeight<HTMLElement>()
  const { state, dispatch } = useStore()
  const navigate = useNavigate()
  const def = KINDS[rec.kind]
  const tkey = titleField(rec.kind)

  return (
    <header
      ref={box}
      className="sticky top-0 z-20 mb-6 border-b pt-1"
      style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}
    >
      {rec.archived && (
        <div
          className="rounded-control text-body mb-2 flex items-center gap-2 border px-3 py-1.5"
          style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
        >
          <Icon name="Archive" size={14} style={{ color: 'var(--muted)' }} />
          <span>This {def.singular.toLowerCase()} is archived.</span>
          <button
            type="button"
            className="ml-auto font-medium"
            style={{ color: 'var(--accent)' }}
            onClick={() => void dispatch({ type: `${rec.kind}.restore`, subject: rec.id })}
          >
            Restore
          </button>
        </div>
      )}

      {/* Three tiers — kind, name, labels — with the spacing squeezed out rather than
          the tiers merged. The header stays this size for the whole scroll. */}
      <div className="flex items-start gap-x-4 pb-2">
        <div className="min-w-0 flex-1">
          {/* Where you are, then what this is. Every record belongs to exactly one
              client, and until now only a project said so — and even that was
              passed to a header Page never drew. */}
          <div className="eyebrow flex flex-wrap items-center gap-1.5 leading-none">
            {crumbs.map((c, i) => (
              <span key={c.to} className="flex items-center gap-1.5">
                {i > 0 && <span style={{ color: 'var(--faint)' }}>/</span>}
                <Link to={c.to} style={{ color: 'var(--accent)' }}>
                  {c.label}
                </Link>
              </span>
            ))}
            {crumbs.length > 0 && <span style={{ color: 'var(--faint)' }}>·</span>}
            <Icon
              name={iconName(def.icon)}
              size={12}
              style={{ color: `var(${accentVar(rec.kind)})` }}
            />
            <span className="eyebrow" style={{ color: `var(${accentVar(rec.kind)})` }}>
              {/* "Work" is the type; a reader wants to know which of its two lives
                  this record is in. See isDelivered. */}
              {rec.kind === 'project'
                ? isDelivered(rec, todayIso())
                  ? 'Artifact'
                  : 'Project'
                : def.singular}
            </span>
          </div>
          <h1
            className="text-page mt-1 font-semibold tracking-tight"
            style={{ textWrap: 'balance', lineHeight: 1.15 }}
          >
            <EditableText
              value={titleOf(state, rec.id)}
              placeholder={def.fields.find((f) => f.title)?.placeholder ?? 'Untitled'}
              onSave={(v) =>
                void dispatch({ type: `${rec.kind}.update`, subject: rec.id, data: { [tkey]: v } })
              }
              style={{ marginLeft: -8 }}
            />
          </h1>
          <div className="mt-1">
            <HeaderLine rec={rec} />
          </div>
        </div>
        <span className="shrink-0 pt-1">
          <ConfirmAction
            title={`Archive this ${def.singular.toLowerCase()}`}
            confirmLabel="Archive"
            onConfirm={() => {
              void dispatch({ type: `${rec.kind}.archive`, subject: rec.id })
              navigate(`/${def.route}`)
            }}
          />
        </span>
      </div>
    </header>
  )
}

/** Stage colour, in one place: money is ember, signed is green, finished is marine. */
function stageColor(kind: StageKind | undefined): string {
  if (kind === 'lost') return 'grey'
  if (kind === 'closed') return 'marine'
  return isCommitted(kind) ? 'green' : 'ember'
}

/** The labels and dates that belong beside the name, all inline-editable. */
function HeaderLine({ rec }: { rec: EntityRecord }) {
  const { state, config, dispatch } = useStore()
  const upd = (data: Record<string, unknown>) =>
    void dispatch({ type: `${rec.kind}.update`, subject: rec.id, data })
  const gap = 'flex flex-wrap items-center gap-x-2 gap-y-1 text-meta'

  if (rec.kind === 'project') {
    return (
      <div className={gap} style={{ color: 'var(--muted)' }}>
        <PillSelect
          options={config.projectKinds}
          value={fieldStr(rec, 'kind')}
          placeholder="kind"
          onPick={(kind) => upd({ kind })}
        />
        <span style={{ color: 'var(--faint)' }}>·</span>
        <span
          className="num inline-flex items-center gap-1"
          style={{ fontSize: 'var(--text-meta)' }}
        >
          <EditableText
            value={fieldStr(rec, 'start')}
            type="date"
            placeholder="start"
            onSave={(start) => upd({ start })}
            style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-meta)' }}
          />
          <span style={{ color: 'var(--faint)' }}>→</span>
          <EditableText
            value={fieldStr(rec, 'end')}
            type="date"
            placeholder="open"
            onSave={(end) => upd({ end })}
            style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-meta)' }}
          />
        </span>
      </div>
    )
  }

  if (rec.kind === 'deal') {
    const stage = config.stages.find((s) => s.key === stageOf(rec, config.stages))
    return (
      <div className={gap} style={{ color: 'var(--muted)' }}>
        <Pill def={{ color: stageColor(stage?.kind) }} label={stage?.label ?? 'unstaged'} />
        {/* A readout: orgs are edited in ONE place, the Orgs block in the aside. */}
        {orgsForDeal(state, rec.id).length > 0 && (
          <span className="inline-flex flex-wrap items-center gap-1">
            for
            {orgsForDeal(state, rec.id).map((o) => (
              <EntityLink key={o.id} id={o.id} />
            ))}
          </span>
        )}
        <span style={{ color: 'var(--faint)' }}>·</span>
        <span className="num" style={{ color: 'var(--ember)', fontSize: 'var(--text-lead)' }}>
          <EditableText
            value={fieldStr(rec, 'value')}
            type="number"
            placeholder="value"
            format={(v) => money(Number(v) || 0, 'value')}
            onSave={(v) => upd({ value: Number(v.replace(/[^0-9.]/g, '')) || 0 })}
            style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-lead)' }}
          />
        </span>
      </div>
    )
  }

  const orgs = linkedOfKind(state, rec.id, 'org')
  return (
    <div className={gap} style={{ color: 'var(--muted)' }}>
      {fieldStr(rec, 'role') && <span>{fieldStr(rec, 'role')}</span>}
      {orgs.map((o) => (
        <EntityLink key={o.id} id={o.id} />
      ))}
    </div>
  )
}

// ── the aside: every fact, condensed ────────────────────────────────────────

function DetailAside({ rec }: { rec: EntityRecord }) {
  const { config, dispatch } = useStore()
  const def = KINDS[rec.kind]
  const upd = (key: string, val: string | number) =>
    void dispatch({ type: `${rec.kind}.update`, subject: rec.id, data: { [key]: val } })

  // Fields the header or the main column already carries, so nothing is edited twice.
  const inHeader: Record<string, string[]> = {
    // `owner` gets its own aside block, so keep it out of the generic Details grid
    // `links` gets its own block, and the header carries the dates and the labels.
    project: [
      'name',
      'kind',
      'start',
      'end',
      'description',
      'context',
      'summary',
      'owner',
      'links',
      'url',
    ],
    org: ['name', 'state', 'description', 'about'],
    deal: ['title', 'org', 'orgs', 'value', 'description', 'links'],
    person: ['name', 'role', 'description', 'about'],
  }
  const skip = new Set(inHeader[rec.kind] ?? ['name'])
  const scalars = def.fields.filter((f) => !skip.has(f.key) && f.type !== 'rich' && !f.detail)
  // On a committed deal the contract dates get their own titled block, so "Signed" and
  // "Ends" don't sit in a list called Details next to "Expected close".
  const committed =
    rec.kind === 'deal' &&
    (isCommitted(config.stages.find((s) => s.key === stageOf(rec, config.stages))?.kind) ||
      config.stages.find((s) => s.key === stageOf(rec, config.stages))?.kind === 'closed')
  const CONTRACT_KEYS = ['signedAt', 'endsAt']
  const contractFields: FieldDef[] = committed
    ? scalars.filter((f) => CONTRACT_KEYS.includes(f.key))
    : []
  const detailFields = scalars.filter(
    (f) =>
      !contractFields.includes(f) &&
      // A forecast stops meaning anything the moment there's a contract, and an unsigned
      // deal has no dates to show, so neither set clutters the other.
      !(committed && f.key === 'expectedClose') &&
      !(!committed && CONTRACT_KEYS.includes(f.key)),
  )

  // The client is the spine of the app, so it is the first thing in every aside
  // rather than the fifth. It was below the rollups, the owner and the links, which
  // on a project with a board meant scrolling past the whole board to find out whose
  // work it was — or to say.
  const orgRel = def.relations?.find((r) => r.kind === 'org')
  const otherRels = def.relations?.filter((r) => r.kind !== 'org') ?? []

  return (
    <div className="text-body">
      {orgRel && (
        <AsideBlock title={orgRel.label}>
          <RelationField entityId={rec.id} targetKind={orgRel.kind} />
        </AsideBlock>
      )}

      <Numbers rec={rec} />

      {/* A deal's stage, its work and its renewals are facts about the record, so they
          sit beside it rather than competing with the prose for the main column. */}
      {/* Stage, value and the renewal chain moved into DealHero — they are the
          point of the page, not facts beside it. What stays here is the work the
          deal pays for, which is a link out. */}
      {rec.kind === 'deal' && <WorksBlock deal={rec} />}

      {/* A project's context: the repo, the shared drive, the recording. Not its
          deliveries — those are artifacts, and they carry their own address. */}
      {rec.kind === 'project' && (
        <AsideBlock title="Links">
          <LinksField
            value={fieldStr(rec, 'links') || fieldStr(rec, 'url')}
            onSave={(v) => upd('links', v)}
          />
        </AsideBlock>
      )}

      {rec.kind === 'project' && (
        <AsideBlock title="Led by">
          <RefPicker
            kind="person"
            value={fieldStr(rec, 'owner')}
            placeholder="who leads this"
            onPick={(owner) => upd('owner', owner)}
            onClear={() => upd('owner', '')}
          />
        </AsideBlock>
      )}

      {contractFields.length > 0 && (
        <AsideBlock title="Contract">
          {contractFields.map((f) => (
            <AsideField key={f.key} def={f} rec={rec} onSave={(v) => upd(f.key, v)} />
          ))}
        </AsideBlock>
      )}

      {/* Where the signed copy lives. Same repeatable field as a work's links. */}
      {rec.kind === 'deal' && (
        <AsideBlock title="Links">
          <LinksField value={fieldStr(rec, 'links')} onSave={(v) => upd('links', v)} />
        </AsideBlock>
      )}

      {detailFields.length > 0 && (
        <AsideBlock title="Details">
          {detailFields.map((f) => (
            <AsideField key={f.key} def={f} rec={rec} onSave={(v) => upd(f.key, v)} />
          ))}
        </AsideBlock>
      )}

      {otherRels.map((rel) => (
        <AsideBlock key={rel.kind} title={rel.label}>
          <RelationField entityId={rec.id} targetKind={rel.kind} />
        </AsideBlock>
      ))}

      {/* One block, not two. Who added this and what points at it are the same
          question — where the record came from — and they were two separate titled
          blocks at the bottom of every aside, each as loud as the facts above. */}
      {/* `right` is the aside's one label-value row; every other block is a label
          OVER its value, and one that is not reads as a different kind of block. The
          word belongs under the heading with the rest of the facts. */}
      <AsideBlock title="Provenance">
        <ActorStamp actor={rec.createdBy} ts={rec.createdAt} prefix="Added by" />
        <div className="mt-2">
          <Backlinks id={rec.id} />
        </div>
      </AsideBlock>
    </div>
  )
}

/** The rollups, as a compact label/value list rather than a card strip. */
function Numbers({ rec }: { rec: EntityRecord }) {
  const { state, config } = useStore()

  if (rec.kind === 'project') {
    const deals = dealsForProject(state, rec.id)
    const booked = deals
      .filter((d) =>
        isCommitted(config.stages.find((s) => s.key === stageOf(d, config.stages))?.kind),
      )
      .reduce((sum, d) => sum + (Number(d.fields.value) || 0), 0)
    const downstream = reuseScore(state, rec.id)
    if (deals.length === 0 && downstream === 0) return null
    return (
      <AsideBlock title="What it has earned">
        <NumberRows
          rows={[
            ['Deals', String(deals.length), '--ember'],
            ['Signed', money(booked, '·'), '--good'],
            ['Builds on this', String(downstream), '--brass'],
          ]}
        />
      </AsideBlock>
    )
  }

  // No org rollup: every `org_` id redirects to /c/:id, so an org never reaches
  // this page. The client page's fact strip is where those numbers live now.

  return null
}

function NumberRows({ rows }: { rows: [string, string, string][] }) {
  return (
    <div className="flex flex-col">
      {rows.map(([label, value, hue]) => (
        <div key={label} className="flex items-baseline gap-2 py-1">
          <span className="text-meta flex-1" style={{ color: 'var(--muted)' }}>
            {label}
          </span>
          <span className="num" style={{ fontSize: 'var(--text-body)', color: `var(${hue})` }}>
            {value}
          </span>
        </div>
      ))}
    </div>
  )
}

/** A rich (@mention-capable) body. Uncontrolled while editing; committed on save. */
export function RichField({
  value,
  placeholder,
  onSave,
}: {
  value: string
  placeholder?: string
  onSave: (v: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  if (editing) {
    return (
      <div className="space-y-2">
        <MentionInput
          value={value}
          onChange={setDraft}
          autoFocus
          minHeight={78}
          placeholder={placeholder}
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              onSave(draft)
              setEditing(false)
            }}
            className="rounded-control text-body px-3 py-1.5 font-medium"
            style={{ background: 'var(--marine)', color: 'var(--panel)' }}
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-body px-2"
            style={{ color: 'var(--muted)' }}
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }
  return (
    <div className="group relative pr-6">
      {value.trim() ? (
        <Markdown body={value} className="prose-body" />
      ) : (
        <span className="text-body" style={{ color: 'var(--faint)' }}>
          {placeholder ?? 'Nothing yet.'}
        </span>
      )}
      <button
        type="button"
        onClick={() => {
          setDraft(value)
          setEditing(true)
        }}
        className="absolute top-0 right-0 opacity-0 group-hover:opacity-60 hover:!opacity-100"
        title="Edit"
      >
        <Icon name="Pencil" size={14} />
      </button>
    </div>
  )
}

// ── project ──────────────────────────────────────────────────────────────────

function ProjectSections({ project }: { project: EntityRecord }) {
  const { state, config, dispatch } = useStore()
  const navigate = useNavigate()
  const create = useCreateEntity()
  const id = project.id
  const deals = dealsForProject(state, id)
  const produced = linkedOfKind(state, id, 'artifact')

  return (
    <>
      <PromisedIn workId={id} />

      {/* What this project has produced. "Came out of" was editable only from the
          artifact's own page, so recording a delivery meant leaving the project you
          were looking at, making the record, and linking back. */}
      <Section
        title="Produced"
        note="artifacts out of this project"
        right={
          <AddRecord
            kind="artifact"
            exclude={produced.map((a) => a.id)}
            onPick={(to) => void dispatch({ type: 'link.add', subject: id, data: { to } })}
            onCreate={(name) => void create('artifact', name, undefined, { links: [id] })}
          />
        }
      >
        {produced.length === 0 ? (
          <p className="text-body py-2" style={{ color: 'var(--faint)' }}>
            Nothing delivered out of this yet.
          </p>
        ) : (
          <div className="rows">
            {produced.map((a) => (
              // Title first, like the Deals rows directly below it. The kind used
              // to lead in a 5.5rem column, so two sibling sections on one page
              // started their names at two different left edges — which is the one
              // thing an outline of ruled rows cannot afford.
              <button
                key={a.id}
                type="button"
                onClick={() => navigate(`/e/${a.id}`)}
                className="flex w-full items-baseline gap-3 py-2.5 text-left"
              >
                <span className="text-body min-w-0 flex-1 font-medium hover:underline">
                  {fieldStr(a, 'name')}
                </span>
                <span className="eyebrow shrink-0">
                  {labelOf(config.artifactKinds, fieldStr(a, 'kind'))?.label ?? '·'}
                </span>
                <span
                  className="num text-meta shrink-0"
                  style={{ color: 'var(--muted)', minWidth: '5.5rem', textAlign: 'right' }}
                >
                  {fieldStr(a, 'delivered') || 'draft'}
                </span>
              </button>
            ))}
          </div>
        )}
      </Section>
      <Section
        title="Deals"
        note="what this project is attached to"
        right={<AttachDeal projectId={id} />}
      >
        {deals.length === 0 ? (
          <p className="text-body py-2" style={{ color: 'var(--faint)' }}>
            No deals attached.
          </p>
        ) : (
          <div className="rows">
            {deals.map((d) => {
              const stage = config.stages.find((s) => s.key === stageOf(d, config.stages))
              return (
                <div key={d.id} className="flex items-center gap-3 py-2.5">
                  <button
                    type="button"
                    onClick={() => navigate(`/e/${d.id}`)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <span className="text-body block truncate font-medium hover:underline">
                      {titleOf(state, d.id) || 'Untitled deal'}
                    </span>
                    <span className="meta">
                      {orgsForDeal(state, d.id)
                        .map((o) => titleOf(state, o.id))
                        .join(', ')}
                    </span>
                  </button>
                  <Pill
                    def={{ color: stageColor(stage?.kind) }}
                    label={stage?.label ?? 'unstaged'}
                  />
                  <span
                    className="num shrink-0"
                    style={{ fontSize: 'var(--text-meta)', color: 'var(--ember)' }}
                  >
                    {money(Number(d.fields.value) || 0, '·')}
                  </span>
                  <ConfirmAction
                    icon="X"
                    title="Detach"
                    confirmLabel="Detach"
                    onConfirm={() =>
                      void dispatch({
                        type: 'support.remove',
                        subject: d.id,
                        data: { project: id },
                      })
                    }
                  />
                </div>
              )
            })}
          </div>
        )}
      </Section>
    </>
  )
}

/**
 * Which deals promised this work, and when it's owed. Read-only on purpose: the
 * commitment belongs to the deal that made it, so this is a window, not a control.
 */
function PromisedIn({ workId }: { workId: string }) {
  const { state, version } = useStore()
  const navigate = useNavigate()
  void version
  const promises = commitmentsForWork(state, workId)
  if (promises.length === 0) return null

  return (
    <Section title="Delivers" note="edited on the deal that promised it">
      <div className="rows">
        {promises.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => navigate(`/e/${c.deal}`)}
            className="flex w-full items-center gap-3 py-2 text-left"
          >
            <span className="min-w-0 flex-1">
              <span className="text-body block truncate">{c.label}</span>
              <span className="meta">{titleOf(state, c.deal) || 'a deal'}</span>
            </span>
            <span
              className="num shrink-0"
              style={{
                fontSize: 'var(--text-label)',
                color: c.deliveredAt ? 'var(--good)' : 'var(--warn)',
              }}
            >
              {c.deliveredAt ? `delivered ${c.deliveredAt}` : c.due || 'no date'}
            </span>
          </button>
        ))}
      </div>
    </Section>
  )
}

/**
 * The record's own corner of the network, drawn with the same force layout as the
 * Network view rather than the old left-to-right lineage strip. Two hops out, because
 * one hop only ever confirms what the sections above already list.
 */
function LocalNetwork({ id }: { id: string }) {
  const { state, version } = useStore()
  const navigate = useNavigate()

  const local = useMemo(() => {
    void version
    return neighbourhood(buildNetwork(state), id, 2)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, id])

  if (local.edges.length === 0) return null

  return (
    <Section title="Network" note={`${local.nodes.length - 1} records around this one · derived`}>
      <div className="card relative overflow-hidden" style={{ height: 300 }}>
        <NetworkCanvas net={local} onOpen={(nid) => navigate(`/e/${nid}`)} focus={id} anchor />
      </div>
    </Section>
  )
}

/** Attach this work to a deal, as support or as output. */
/** One control, no mode, no role: pick a deal and it's attached. */
function AttachDeal({ projectId }: { projectId: string }) {
  const { dispatch } = useStore()
  return (
    <RefPicker
      kind="deal"
      value=""
      placeholder="attach a deal"
      onPick={(dealId) =>
        void dispatch({ type: 'support.add', subject: dealId, data: { project: projectId } })
      }
    />
  )
}

// ── org ──────────────────────────────────────────────────────────────────────

// ── deal ─────────────────────────────────────────────────────────────────────

/** The work on a deal: one list, no roles, plus the two ways to add to it. */
// OrgSections lived above: a second implementation of the client page's Deals and
// Projects sections, unreachable since `org_` ids started redirecting to /c/:id.

function WorksBlock({ deal }: { deal: EntityRecord }) {
  const { state, config, dispatch } = useStore()
  const navigate = useNavigate()
  const id = deal.id
  const works = projectsForDeal(state, id)

  /** Committing to a deal should be able to start the work, pre-linked. */
  const spawn = () => {
    const pid = newId('prj')
    void dispatch([
      {
        type: 'project.create',
        subject: pid,
        data: {
          name: titleOf(state, id) || 'New work',
          kind: 'artifact',
          state: 'available',
          start: todayIso(),
        },
      },
      { type: 'support.add', subject: id, data: { project: pid } },
    ])
    navigate(`/e/${pid}`)
  }

  return (
    <AsideBlock title="Projects" right={<AttachProject dealId={id} />}>
      {works.length === 0 ? (
        <p className="text-meta" style={{ color: 'var(--faint)' }}>
          None attached.{' '}
          <button
            type="button"
            onClick={spawn}
            className="hover:underline"
            style={{ color: 'var(--marine)' }}
          >
            New work from this deal
          </button>
          .
        </p>
      ) : (
        <div className="rows">
          {works.map((w) => {
            const kind = labelOf(config.projectKinds, fieldStr(w, 'kind'))
            return (
              <div key={w.id} className="flex items-center gap-2 py-1.5">
                <button
                  type="button"
                  onClick={() => navigate(`/e/${w.id}`)}
                  className="text-body min-w-0 flex-1 truncate text-left hover:underline"
                >
                  {titleOf(state, w.id) || 'Untitled work'}
                </button>
                {kind && <Pill def={kind} label={kind.label} />}
                <ConfirmAction
                  icon="X"
                  title="Detach"
                  confirmLabel="Detach"
                  onConfirm={() =>
                    void dispatch({ type: 'support.remove', subject: id, data: { project: w.id } })
                  }
                />
              </div>
            )
          })}
        </div>
      )}
    </AsideBlock>
  )
}

/** Same on the other side: pick the work, it's attached. */
function AttachProject({ dealId }: { dealId: string }) {
  const { dispatch } = useStore()
  return (
    <RefPicker
      kind="project"
      value=""
      placeholder="attach project"
      onPick={(project) =>
        void dispatch({ type: 'support.add', subject: dealId, data: { project } })
      }
    />
  )
}

// ── person ───────────────────────────────────────────────────────────────────

/**
 * A project's board, on the project's own page.
 *
 * The board used to live only at /b/:id, linked from a client page and nowhere
 * else, so a project opened from the Projects tab showed a description and a
 * timeline and gave no hint that eleven items existed. One page per project, with
 * the board on it, is the fix; /b/:id stays as a focused full-width view.
 */
function ProjectBoard({ project }: { project: EntityRecord }) {
  const { state, version } = useStore()
  void version
  const groups = groupsForWork(state, project.id)
  const counts = shownCount(state, project.id)

  return (
    <Section
      title="Board"
      note={
        groups.length === 0
          ? 'none yet — add a group and this becomes a board'
          : `${groups.length} ${groups.length === 1 ? 'group' : 'groups'} · ${counts.shown}/${counts.total} shown to client`
      }
      right={
        groups.length > 0 ? (
          <Link to={`/b/${project.id}`} className="meta underline">
            full width
          </Link>
        ) : undefined
      }
    >
      <Board workId={project.id} />
    </Section>
  )
}
